import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { crmService, customerService } from '../../../services/api';
import { groupCatalog } from './permissionGroups';

// Every service that gates endpoints on role codes, in display order. A new
// one is added here once it serves GET /permissions/ (docs/permission_rollout.md).
const CATALOG_SOURCES = [
  { service: 'customer', fetch: customerService.getPermissionsCatalog },
  { service: 'crm', fetch: crmService.getPermissionsCatalog },
];

// The catalogs only change with a deploy.
const CATALOG_STALE_TIME = 60 * 60 * 1000;

export default function usePermissionsCatalog() {
  const { i18n } = useTranslation();
  const queries = useQueries({
    queries: CATALOG_SOURCES.map(({ service, fetch }) => ({
      // Labels come translated (Accept-Language): a language switch refetches.
      queryKey: ['permissionsCatalog', service, i18n.language],
      queryFn: () => fetch().then((res) => res.data.permissions),
      staleTime: CATALOG_STALE_TIME,
    })),
  });

  const entries = queries.flatMap((query) => query.data ?? []);
  const failedServices = CATALOG_SOURCES.filter((_, index) => queries[index].isError).map(
    ({ service }) => service,
  );

  return {
    groups: groupCatalog(entries),
    codes: entries.map((entry) => entry.code),
    isLoading: queries.some((query) => query.isLoading),
    // Only a total failure blocks the page; a partial one shows a warning.
    isError: failedServices.length === CATALOG_SOURCES.length,
    failedServices,
    refetch: () => queries.forEach((query) => query.refetch()),
  };
}
